import Loading from '$components/Loading'
import { viewButton } from '$components/Table/styles.css'
import { useAssignRoleStore } from '$hooks/stores'
import { TAppRoleListResponse, TID } from '$services/api'
import { TRoles } from '$services/types'
import {
  customTableCell,
  customTableContainer,
  customTableHead
} from '$styles/common.css'
import { Checkbox } from '@genie-fintech/ui/components'
import {
  table,
  tableBody,
  tableContainerInner,
  tableRow
} from '@genie-fintech/ui/style/element'
import { Dispatch, FC, SetStateAction, useCallback } from 'react'

type Props = {
  data: TRoles[]
  isLoading?: boolean
  onChange: (id: TID[]) => void
  roles: TID[]
  currentPage: number
  perPage: number
  setSelectedRole: Dispatch<
    SetStateAction<
      | {
          id: number
          name: string
        }
      | undefined
    >
  >
}

export const AssignTable: FC<Props> = ({
  data,
  isLoading,
  roles,
  onChange,
  setSelectedRole,
  currentPage,
  perPage
}) => {
  const { addRoles, removeRoleList, removeRole } = useAssignRoleStore()

  const columns: {
    key: keyof Omit<TAppRoleListResponse['data'][number], 'id'> | 'permission'
    value: string
  }[] = [
    { key: 'name', value: 'ROLE NAME' },
    { key: 'display_name', value: 'DISPLAY NAME' },
    { key: 'permission', value: 'PERMISSION' },
    { key: 'country', value: 'COUNTRY' },
    { key: 'group', value: 'GROUP' }
  ]

  const handleCheckAll = useCallback(
    (checked: boolean) => {
      {
        if (checked) {
          const AllRoles = [...roles, ...data.map(v => v.id)]
          const newData = data.filter(d => !d.isDisabled) // remove disabled roles or already selected role
          addRoles(
            newData.map(v => ({
              ...v,
              isDisabled: false
            }))
          )
          const uniqueNumbers = [...new Set(AllRoles)] // remove duplicates
          onChange(uniqueNumbers)
          return
        }

        const removeData = data.filter(d => !d.isDisabled) // remove disabled roles or already selected role
        removeRoleList(removeData)
        onChange(
          roles.filter(roleId => {
            const role = data.find(r => r.id === +roleId)
            return !(role && !role.isDisabled) // keep it only if it IS disabled or not found
          })
        )
      }
    },
    [addRoles, data, onChange, removeRoleList, roles]
  )

  return (
    <>
      <article className={customTableContainer}>
        <main className={tableContainerInner}>
          <table className={table({ separator: 'line' })}>
            <thead className={customTableHead}>
              <tr className={tableRow}>
                <th style={{ width: 0 }} className={customTableCell}>
                  <Checkbox
                    boxProps={{
                      checked:
                        !!data.length && data.every(d => roles.includes(d.id)),
                      onCheckedChange: handleCheckAll
                    }}
                  />
                </th>
                <th className={customTableCell} style={{ width: 0 }}>
                  NO
                </th>
                {columns.map((col, key) => (
                  <th key={key} className={customTableCell}>
                    {col.value}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody className={tableBody}>
              {data.map((d, rowKey) => (
                <tr key={rowKey} className={tableRow}>
                  <td className={customTableCell}>
                    <Checkbox
                      disabled={!onChange || d.isDisabled}
                      boxProps={{
                        checked: roles.includes(d.id),
                        onCheckedChange: checked => {
                          if (checked) {
                            addRoles([{ ...d, isDisabled: false }])
                            onChange([...roles, d.id])
                            return
                          }
                          removeRole(d)
                          onChange(roles.filter(r => r !== d.id))
                        }
                      }}
                    />
                  </td>
                  <td className={customTableCell}>
                    {(currentPage - 1) * perPage + rowKey + 1}
                  </td>
                  {columns.map((col, colKey) => (
                    <td key={colKey} className={customTableCell}>
                      {(() => {
                        if (col.key === 'group') {
                          return d.group.name
                        }

                        if (col.key === 'country') {
                          return d.country?.name ?? '-'
                        }

                        if (col.key === 'permission') {
                          return (
                            <button
                              type="button"
                              onClick={() => {
                                setSelectedRole({
                                  id: d.id,
                                  name: d.name
                                })
                              }}
                              style={{ fontSize: 14, fontWeight: 500 }} // to replace with typography class
                              className={viewButton}
                            >
                              View
                            </button>
                          )
                        }

                        return d[col.key] ?? 'N/A'
                      })()}
                    </td>
                  ))}
                </tr>
              ))}

              {!data.length && !isLoading && (
                <tr className={tableRow}>
                  <td
                    colSpan={columns.length + 2}
                    style={{ textAlign: 'center' }}
                    className={customTableCell}
                  >
                    NO DATA
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </main>

        {isLoading && <Loading />}
      </article>
    </>
  )
}
